"use client";

import { useEffect, useMemo, useState } from "react";
import {
  Checkbox,
  ErrorNotice,
  Notice,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SMALL,
  HINT,
} from "@/components/tool-kit/styles";
import LadderChecker from "@/components/tools/games-organizers/word-ladder-checker";
import LadderOutput from "@/components/tools/games-organizers/word-ladder-output";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  checkEnds,
  findLadder,
  type InputProblem,
  ladderIndexFor,
  MAX_LADDER_LENGTH,
  parseWordInput,
  suggestPair,
} from "@/lib/tool-kit/games-organizers/word-ladder";
import { loadWordList } from "@/lib/tool-kit/vocabulary/anagram";
import { createRng, newSeed } from "@/lib/tool-kit/worksheets/random";

/** English strings. A translated content file overrides these through `ui`. */
export const WORD_LADDER_GENERATOR_STRINGS = {
  startLabel: "Start word",
  startPlaceholder: "cold",
  endLabel: "End word",
  endPlaceholder: "warm",
  endsHint:
    "Two real English words with the same number of letters (2–{max}). Press Enter or just type; the ladder appears below.",
  bannedLabel: "Words to avoid (optional)",
  bannedHint:
    "Separate with spaces or commas. The ladder will not step on these words.",
  everyday: "Prefer everyday words",
  everydayHint:
    "For 2–5 letter words the ladder leans on a hand-picked list of common words and leaves out crude ones. Longer words use the whole list.",
  suggestHeading: "Need a pair of words?",
  suggestLength: "Word length",
  suggestSteps: "Steps",
  lengthOption: "one:{count} letter|other:{count} letters",
  stepsOption: "one:{count} step|other:{count} steps",
  suggest: "Suggest a pair",
  suggestNone:
    "No pair that far apart turned up. Try another length or number of steps.",
  loading: "Loading the word list (once per visit)…",
  loadError:
    "Could not load the word list. Check your connection and try again.",
  retry: "Try again",
  problems: {
    empty: "Enter a start word and an end word.",
    letters: "Use the letters a–z only.",
    length: "The two words need the same number of letters.",
    same: "Choose two different words.",
    "too-short": "Use words of at least 2 letters.",
    "too-long": "Use words of at most {max} letters.",
    "unknown-start": "{word} is not in the word list.",
    "unknown-end": "{word} is not in the word list.",
  },
  noLadder:
    "No ladder joins {start} and {end}. Remove a word from the avoid list, or try different words.",
  heading: "Your word ladder",
  summary: "{start} to {end} in {steps}",
  steps: "one:{count} step|other:{count} steps",
  shortestNote:
    "Using every word in the list, the shortest ladder is {steps}; this one leans on everyday words.",
  countNote:
    "one:Only one ladder of {steps} exists with every word in the list.|other:{count} different ladders of {steps} exist with every word in the list.",
  countCapped:
    "More than {count} different ladders of {steps} exist with every word in the list.",
  unfamiliarNote:
    "Not on the everyday list: {words}. They are real words, but pupils may not know them.",
  avoidThese: "Search again without them",
  hintLabel: "Hints on the worksheet",
  hints: {
    none: "No hints",
    mark: "Mark the letter that changes",
    fill: "Fill in every letter except the one that changes",
  },
  extraLabel: "Extra blank rungs",
  nameLine: "Name and date line",
  showKey: "Show answer key",
  hideKey: "Hide answer key",
  printSheet: "Print worksheet",
  printBoth: "Print worksheet and key",
  sheetTitle: "Word Ladder",
  instructions:
    "Change one letter at a time to turn {start} into {end}. Every rung must be a real word.",
  goal: "Can you do it in {steps}?",
  rungs: "Ladder from {start} to {end}",
  keyTitle: "Answer key: {start} to {end}",
  keyNote:
    "The letter that changes at each rung is underlined. Other ladders may also be correct: check each rung is a real word.",
  name: "Name",
  date: "Date",
  checkHeading: "Check a ladder",
  checkLabel: "Your ladder — one word per line, start word first",
  checkHint: "Type or paste a ladder from {start} to {end}.",
  checkPlaceholder: "cold\ncord\nword\nward\nwarm",
  checkDone: "A valid ladder from {start} to {end}.",
  checkSoFar: "Every rung is valid so far, but it does not reach {end} yet.",
  checkFix: "Some rungs need fixing.",
  stepStatus: {
    ok: "valid",
    notAWord: "not in the word list",
    badChange: "must change exactly one letter",
    repeated: "already used",
  },
  sourceNote:
    "Words are checked against ENABLE, a public-domain word-game list that includes some rare words, so a valid ladder can step on a word you do not know. Nothing you type leaves your browser.",
} as const;

type Strings = typeof WORD_LADDER_GENERATOR_STRINGS;
type ListState =
  | { status: "idle" | "loading" | "error" }
  | { status: "ready"; words: string[] };

const LENGTHS = [3, 4, 5] as const;
const STEP_CHOICES = [2, 3, 4, 5, 6, 7, 8] as const;

function problemText(
  strings: Strings,
  problem: InputProblem,
  start: string,
  end: string
): string {
  const word = problem === "unknown-start" ? start : end;
  return t(strings.problems[problem], { max: MAX_LADDER_LENGTH, word });
}

export default function WordLadderGeneratorWidget() {
  const strings = useToolUi(WORD_LADDER_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [bannedText, setBannedText] = useState("");
  const [everyday, setEveryday] = useState(true);
  const [suggestLength, setSuggestLength] = useState<number>(4);
  const [suggestSteps, setSuggestSteps] = useState<number>(4);
  const [suggestMissed, setSuggestMissed] = useState(false);
  const [pendingSuggest, setPendingSuggest] = useState(false);
  const [list, setList] = useState<ListState>({ status: "idle" });

  const wantList = start.trim() !== "" || end.trim() !== "" || pendingSuggest;

  useEffect(() => {
    if (!wantList || list.status !== "idle") {
      return;
    }
    setList({ status: "loading" });
    loadWordList()
      .then((words) => setList({ status: "ready", words }))
      .catch(() => setList({ status: "error" }));
  }, [wantList, list.status]);

  useEffect(() => {
    if (!(pendingSuggest && list.status === "ready")) {
      return;
    }
    setPendingSuggest(false);
    const pair = suggestPair(
      list.words,
      { length: suggestLength, steps: suggestSteps },
      createRng(newSeed())
    );
    setSuggestMissed(pair === null);
    if (pair) {
      setStart(pair.start);
      setEnd(pair.end);
    }
  }, [pendingSuggest, list, suggestLength, suggestSteps]);

  const words = list.status === "ready" ? list.words : null;
  const length = start.trim().length;
  const index = useMemo(
    () =>
      words && length >= 2 && length <= MAX_LADDER_LENGTH
        ? ladderIndexFor(words, length)
        : null,
    [words, length]
  );
  const ends = checkEnds(start, end, index);
  const banned = useMemo(
    () => new Set(parseWordInput(bannedText)),
    [bannedText]
  );
  const search = useMemo(
    () =>
      words && ends.problem === null
        ? findLadder(words, ends.start, ends.end, { everyday, banned })
        : null,
    [words, ends.problem, ends.start, ends.end, everyday, banned]
  );
  const ladder = search?.ladder ?? null;

  const showProblem =
    ends.problem !== null &&
    ends.problem !== "empty" &&
    (words !== null ||
      !["unknown-start", "unknown-end"].includes(ends.problem));

  return (
    <div className="space-y-6">
      <div className="space-y-4 print:hidden">
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="wl-start"
            label={strings.startLabel}
            mono
            onChange={setStart}
            placeholder={strings.startPlaceholder}
            value={start}
          />
          <TextField
            id="wl-end"
            label={strings.endLabel}
            mono
            onChange={setEnd}
            placeholder={strings.endPlaceholder}
            value={end}
          />
        </div>
        <p className={HINT}>
          {t(strings.endsHint, { max: MAX_LADDER_LENGTH })}
        </p>
        <TextField
          hint={strings.bannedHint}
          id="wl-banned"
          label={strings.bannedLabel}
          mono
          onChange={setBannedText}
          value={bannedText}
        />
        <div className="space-y-1">
          <Checkbox
            checked={everyday}
            label={strings.everyday}
            onChange={setEveryday}
          />
          <p className={HINT}>{strings.everydayHint}</p>
        </div>
        <fieldset className="space-y-3 rounded-lg border border-border p-4">
          <legend className="px-1 font-medium text-foreground text-sm">
            {strings.suggestHeading}
          </legend>
          <div className="grid gap-4 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
            <SelectField
              id="wl-suggest-length"
              label={strings.suggestLength}
              onChange={(value) => setSuggestLength(Number(value))}
              options={LENGTHS.map((n) => ({
                value: String(n),
                label: tPlural(strings.lengthOption, locale, n, {
                  count: fmt(n),
                }),
              }))}
              value={String(suggestLength)}
            />
            <SelectField
              id="wl-suggest-steps"
              label={strings.suggestSteps}
              onChange={(value) => setSuggestSteps(Number(value))}
              options={STEP_CHOICES.map((n) => ({
                value: String(n),
                label: tPlural(strings.stepsOption, locale, n, {
                  count: fmt(n),
                }),
              }))}
              value={String(suggestSteps)}
            />
            <button
              className={BUTTON_PRIMARY}
              onClick={() => {
                setSuggestMissed(false);
                setPendingSuggest(true);
              }}
              type="button"
            >
              {strings.suggest}
            </button>
          </div>
          {suggestMissed ? <Notice>{strings.suggestNone}</Notice> : null}
        </fieldset>
      </div>

      <div aria-live="polite" className="space-y-3 print:hidden">
        {list.status === "loading" ? <Notice>{strings.loading}</Notice> : null}
        {list.status === "error" ? (
          <div className="space-y-2">
            <ErrorNotice>{strings.loadError}</ErrorNotice>
            <button
              className={BUTTON_SMALL}
              onClick={() => setList({ status: "idle" })}
              type="button"
            >
              {strings.retry}
            </button>
          </div>
        ) : null}
        {showProblem && ends.problem ? (
          <Notice>
            {problemText(strings, ends.problem, ends.start, ends.end)}
          </Notice>
        ) : null}
        {search && !ladder ? (
          <Notice>
            {t(strings.noLadder, { start: ends.start, end: ends.end })}
          </Notice>
        ) : null}
      </div>

      {search && ladder ? (
        <LadderOutput
          end={ends.end}
          ladder={ladder}
          search={search}
          setBannedText={setBannedText}
          start={ends.start}
          strings={strings}
        />
      ) : null}

      {index && ends.problem === null ? (
        <LadderChecker
          end={ends.end}
          index={index}
          start={ends.start}
          strings={strings}
        />
      ) : null}
      <p className={`${HINT} print:hidden`}>{strings.sourceNote}</p>
    </div>
  );
}
